import { Loading } from '@components';
import { persistor, store } from '@store/store';
import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom';
// ** Redux Imports
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import './index.css';
import '@assets/scss/style.scss';

import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { ConfigProvider } from 'antd';
import moment from 'moment';
import 'moment/locale/id';
import id_ID from 'antd/lib/locale/id_ID';
import './i18n-init';
import { Worker } from '@react-pdf-viewer/core';
import { requestAndSyncFcmToken, requestFcmToken, onForegroundMessage } from './utils/fcm';
import { notificationsApi } from './redux/services/notifications';
import { extractNotificationId } from './utils/notificationRouting';

const SM_NOTIF_ID_QUERY = 'sm_notif_id';

function markNotificationReadFromFcmData(data) {
  const notifId = extractNotificationId(data);
  if (notifId) {
    try {
      store.dispatch(notificationsApi.endpoints.readNotifications.initiate({ ids: [String(notifId)] }));
    } catch {
      // ignore — invalidation below will still refresh caches
    }
    return;
  }

  try {
    store.dispatch(
      notificationsApi.util.invalidateTags([
        { type: 'Notifications', id: 'LIST' },
        { type: 'NotificationsCount', id: 'COUNT' },
      ])
    );
  } catch {
    // ignore
  }
}

function consumeNotifIdFromUrl() {
  try {
    const url = new URL(window.location.href);
    const notifId = url.searchParams.get(SM_NOTIF_ID_QUERY);
    if (!notifId) return;
    url.searchParams.delete(SM_NOTIF_ID_QUERY);
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    markNotificationReadFromFcmData({ id: notifId });
  } catch {
    // ignore
  }
}

if (typeof window !== 'undefined') {
  consumeNotifIdFromUrl();

  if (navigator.serviceWorker) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      const payload = event?.data;
      if (!payload || payload.type !== 'SM_NOTIF_CLICK') return;

      const isDisabled = (() => {
        try {
          return localStorage.getItem('sm_disable_notifications') === '1';
        } catch {
          return false;
        }
      })();
      if (isDisabled) return;

      const data = payload.data || {};
      const targetUrl = payload.targetUrl || (data && data.__sm_target_url) || '/notifikasi';

      // BNS-2892: mark the clicked notification as read so badge count updates.
      markNotificationReadFromFcmData(data);

      try {
        if (typeof targetUrl === 'string' && targetUrl.length > 0) {
          window.location.assign(targetUrl);
        }
      } catch {
        // ignore
      }
    });
  }

  console.log('[FCM] helper installed: call await window.__getFcmToken__()');
  window.__getFcmToken__ = async () => {
    const token = await requestFcmToken();
    // eslint-disable-next-line no-console
    console.log('[FCM] token:', token);
    return token;
  };
  console.log('[FCM] helper installed: call await window.__syncFcmToken__()');
  window.__syncFcmToken__ = async () => {
    const token = await requestAndSyncFcmToken();
    // eslint-disable-next-line no-console
    console.log('[FCM] synced token:', token);
    return token;
  };
  onForegroundMessage((payload) => {
    // eslint-disable-next-line no-console
    console.log('[FCM] foreground message:', payload);
    // App is open — only refresh badge/list counts.
    // OS notification is background-only (SW onBackgroundMessage).
    // Showing showNotification here causes duplicates when FCM SDK + SW both fire.
    try {
      store.dispatch(
        notificationsApi.util.invalidateTags([
          { type: 'Notifications', id: 'LIST' },
          { type: 'NotificationsCount', id: 'COUNT' },
        ])
      );
    } catch {
      // ignore
    }
  });
}

moment().locale('id');
const LazyApp = lazy(() => import('./views/App'));
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  integrations: [new Integrations.BrowserTracing()],
  defaultIntegrations: false,
  beforeSend: (err) => err,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  environment: process.env.REACT_APP_URL_ENV,
  debug: false,
  tracesSampleRate: 1.0,
});

// BNS-2890: App root (version check lives in AppVersionGuard inside Router)
function AppRoot() {
  return (
    <ConfigProvider locale={id_ID}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <Suspense fallback={<Loading />}>
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js">
              <LazyApp />
            </Worker>
          </Suspense>
        </PersistGate>
      </Provider>
    </ConfigProvider>
  );
}

ReactDOM.render(<AppRoot />, document.getElementById('root'));
